import { z } from 'zod';

import {
  EmailSchema,
  IsoDateTimeSchema,
  StoredEmailSchema,
  UuidSchema,
  BooleanQueryParamSchema,
} from './common.js';
import { PaginationQuerySchema, paginatedResponseSchema } from './pagination.js';

/**
 * Customer shapes for the CRM customer master (Phase 3).
 *
 * Layered:
 *   - CustomerListItem  - light row for grids/search dropdowns
 *   - CustomerDetail    - the 360 page (addresses + contacts + documents joined)
 *   - Create/Update     - admin/agent inputs; document files come in via the
 *                         separate `/documents/upload-init` -> `/finalize`
 *                         flow so the API server never proxies binary
 *
 * Identity-number formats (PAN / GST) are validated here so a typo never
 * reaches the DB partial-unique index. The CHECK constraint
 * `customers_corporate_requires_company_name` lives in the migration to
 * mirror the business rule below.
 */

// ---------------------------------------------------------------------------
// Enums (must mirror prisma/schema.prisma)
// ---------------------------------------------------------------------------

export const CustomerKindSchema = z.enum(['INDIVIDUAL', 'CORPORATE']);
export type CustomerKind = z.infer<typeof CustomerKindSchema>;

export const GenderSchema = z.enum(['MALE', 'FEMALE', 'OTHER', 'PREFER_NOT_TO_SAY']);
export type Gender = z.infer<typeof GenderSchema>;

export const MaritalStatusSchema = z.enum(['SINGLE', 'MARRIED', 'DIVORCED', 'WIDOWED', 'OTHER']);
export type MaritalStatus = z.infer<typeof MaritalStatusSchema>;

export const AddressKindSchema = z.enum(['HOME', 'WORK', 'BILLING', 'SHIPPING', 'OTHER']);
export type AddressKind = z.infer<typeof AddressKindSchema>;

export const CustomerDocumentKindSchema = z.enum([
  'PASSPORT',
  'VISA',
  'PAN',
  'AADHAAR',
  'GST_CERTIFICATE',
  'COMPANY_REGISTRATION',
  'OTHER_ID',
  'INVOICE',
  'OTHER',
]);
export type CustomerDocumentKind = z.infer<typeof CustomerDocumentKindSchema>;

// ---------------------------------------------------------------------------
// Identity-number format validators (server-side enforced by partial uniques)
// ---------------------------------------------------------------------------

// PAN: 5 uppercase letters + 4 digits + 1 uppercase letter (e.g. AABCT1234D).
// citext on the column lets us accept lowercase input; we just normalize.
const PanNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{5}[0-9]{4}[A-Z]$/u, 'PAN must be 10 chars: AAAAA9999A');

// GST: 15-char GSTIN. First 2 digits = state code, next 10 = PAN, then entity
// number + checksum + 'Z'. Loose pattern (length + alphanumerics) -- the
// detailed checksum check belongs in a server-side validator, not the schema.
const GstNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[0-9A-Z]{3}$/u, 'GSTIN must be 15 chars');

// Passport: country-dependent. We only require non-empty + length 6-20 + no
// whitespace -- stricter checks (Indian: 1 letter + 7 digits, US: 9 digits)
// are deferred to a country-aware validator in Phase 6 when we add the
// `issuing_country`-aware booking flow.
const PassportNumberSchema = z
  .string()
  .trim()
  .toUpperCase()
  .min(6)
  .max(20)
  .regex(/^[A-Z0-9]+$/u, 'Passport may only contain letters and digits');

// E.164-ish: country code is just digits (no +), national number 6-15 digits.
// The legacy Indian numbers are 10 digits without country code; we accept that
// and let mobile_country fill in "91" downstream.
export const MobileCountrySchema = z
  .string()
  .trim()
  .regex(/^[0-9]{1,4}$/u, 'Country code must be 1-4 digits, e.g. 91');
export const MobileNumberSchema = z
  .string()
  .trim()
  .regex(/^[0-9]{6,15}$/u, 'Mobile must be 6-15 digits, no spaces');

// ---------------------------------------------------------------------------
// Reference sub-schemas (mirror the joins the API exposes)
// ---------------------------------------------------------------------------

const NamedRefSchema = z.object({
  id: UuidSchema,
  name: z.string(),
});
const CountryRefSchema = z.object({
  id: UuidSchema,
  isoCode: z.string(),
  name: z.string(),
});

// ---------------------------------------------------------------------------
// Customer address
// ---------------------------------------------------------------------------

export const CustomerAddressSchema = z.object({
  id: UuidSchema,
  kind: AddressKindSchema,
  isPrimary: z.boolean(),
  line1: z.string(),
  line2: z.string().nullable(),
  landmark: z.string().nullable(),
  pincode: z.string().nullable(),
  city: NamedRefSchema.nullable(),
  cityName: z.string().nullable(),
  state: NamedRefSchema.nullable(),
  stateName: z.string().nullable(),
  country: CountryRefSchema,
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CustomerAddress = z.infer<typeof CustomerAddressSchema>;

export const CustomerAddressInputSchema = z
  .object({
    kind: AddressKindSchema.default('HOME'),
    isPrimary: z.boolean().default(false),
    line1: z.string().min(1).max(255),
    line2: z.string().max(255).nullable().optional(),
    landmark: z.string().max(120).nullable().optional(),
    pincode: z.string().max(20).nullable().optional(),
    cityId: UuidSchema.nullable().optional(),
    cityName: z.string().max(120).nullable().optional(),
    stateId: UuidSchema.nullable().optional(),
    stateName: z.string().max(120).nullable().optional(),
    countryId: UuidSchema.describe('Country UUID. Required; use GET /countries.'),
  })
  .refine((v) => v.cityId !== undefined || v.cityName !== undefined, {
    message: 'Provide either cityId or cityName for the address',
    path: ['cityId'],
  });
export type CustomerAddressInput = z.infer<typeof CustomerAddressInputSchema>;

// ---------------------------------------------------------------------------
// Customer contact (secondary people)
// ---------------------------------------------------------------------------

export const CustomerContactSchema = z.object({
  id: UuidSchema,
  relationship: z.string(),
  salutation: z.string().nullable(),
  firstName: z.string(),
  lastName: z.string().nullable(),
  email: StoredEmailSchema.nullable(),
  mobileCountry: z.string().nullable(),
  mobileNumber: z.string().nullable(),
  dateOfBirth: z.iso.date().nullable(),
  gender: GenderSchema.nullable(),
  notes: z.string().nullable(),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CustomerContact = z.infer<typeof CustomerContactSchema>;

export const CustomerContactInputSchema = z.object({
  relationship: z.string().min(1).max(60),
  salutation: z.string().max(20).nullable().optional(),
  firstName: z.string().min(1).max(80),
  lastName: z.string().max(80).nullable().optional(),
  email: EmailSchema.nullable().optional(),
  mobileCountry: MobileCountrySchema.nullable().optional(),
  mobileNumber: MobileNumberSchema.nullable().optional(),
  dateOfBirth: z.iso.date().nullable().optional(),
  gender: GenderSchema.nullable().optional(),
  notes: z.string().max(2000).nullable().optional(),
});
export type CustomerContactInput = z.infer<typeof CustomerContactInputSchema>;

// ---------------------------------------------------------------------------
// Customer document
// ---------------------------------------------------------------------------

export const CustomerDocumentSchema = z.object({
  id: UuidSchema,
  documentKind: CustomerDocumentKindSchema,
  isPrimary: z.boolean(),
  title: z.string(),
  documentNumber: z.string().nullable(),
  issuedCountry: CountryRefSchema.nullable(),
  issueDate: z.iso.date().nullable(),
  expiryDate: z.iso.date().nullable(),
  mimeType: z.string(),
  sizeBytes: z.number().int().positive(),
  uploadedBy: z.object({ id: UuidSchema, fullName: z.string() }),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
});
export type CustomerDocument = z.infer<typeof CustomerDocumentSchema>;

// Two-step upload flow:
//   1. POST /customers/:id/documents/upload-init  - server returns presigned
//      PUT URL + the eventual s3_key + upload token.
//   2. Client PUTs the file directly to S3 with that URL.
//   3. POST /customers/:id/documents/upload-finalize - server hashes
//      (re-checks size + sha256), creates the customer_documents row.
//
// Avoids passing the binary through the API server -- so document size is
// gated only by S3, not by API memory/CPU.

export const DocumentUploadInitRequestSchema = z.object({
  documentKind: CustomerDocumentKindSchema,
  filename: z
    .string()
    .min(1)
    .max(255)
    .describe('Original filename. Used only to pick the extension for the S3 key.'),
  mimeType: z.string().min(3).max(120),
  sizeBytes: z
    .number()
    .int()
    .positive()
    .max(25 * 1024 * 1024, 'Max 25 MB per document'),
});
export type DocumentUploadInitRequest = z.infer<typeof DocumentUploadInitRequestSchema>;

export const DocumentUploadInitResponseSchema = z.object({
  uploadToken: z
    .string()
    .describe(
      'Opaque token. Pass back unchanged to /upload-finalize so the server can resolve the staged S3 key.',
    ),
  presignedUrl: z.url(),
  s3Bucket: z.string(),
  s3Key: z.string(),
  expiresAt: IsoDateTimeSchema,
});
export type DocumentUploadInitResponse = z.infer<typeof DocumentUploadInitResponseSchema>;

export const DocumentUploadFinalizeRequestSchema = z.object({
  uploadToken: z.string(),
  isPrimary: z.boolean().default(false),
  title: z.string().min(1).max(200),
  documentNumber: z.string().max(60).nullable().optional(),
  issuedCountryId: UuidSchema.nullable().optional(),
  issueDate: z.iso.date().nullable().optional(),
  expiryDate: z.iso.date().nullable().optional(),
  checksumSha256: z
    .string()
    .regex(/^[a-f0-9]{64}$/u, 'SHA-256 must be 64 lowercase hex chars')
    .nullable()
    .optional()
    .describe('Client-computed checksum; server verifies on download.'),
});
export type DocumentUploadFinalizeRequest = z.infer<typeof DocumentUploadFinalizeRequestSchema>;

export const DocumentDownloadResponseSchema = z.object({
  presignedUrl: z.url(),
  expiresAt: IsoDateTimeSchema,
  filename: z.string(),
});
export type DocumentDownloadResponse = z.infer<typeof DocumentDownloadResponseSchema>;

// ---------------------------------------------------------------------------
// Customer list + detail
// ---------------------------------------------------------------------------

export const CustomerListItemSchema = z.object({
  id: UuidSchema,
  kind: CustomerKindSchema,
  displayName: z.string(),
  email: StoredEmailSchema.nullable(),
  mobileCountry: z.string().nullable(),
  mobileNumber: z.string().nullable(),
  companyName: z.string().nullable(),
  gstNumber: z.string().nullable(),
  branch: NamedRefSchema.nullable(),
  nationality: CountryRefSchema.nullable(),
  isActive: z.boolean(),
  createdAt: IsoDateTimeSchema,
});
export type CustomerListItem = z.infer<typeof CustomerListItemSchema>;

export const CustomerListQuerySchema = PaginationQuerySchema.extend({
  /** Free-text fuzzy search (trigram index across name/email/mobile/passport). */
  q: z.string().trim().min(1).max(120).optional(),
  kind: CustomerKindSchema.optional(),
  branchId: UuidSchema.optional(),
  nationalityCountryId: UuidSchema.optional(),
  isActive: BooleanQueryParamSchema.optional(),
  includeDeleted: BooleanQueryParamSchema.optional(),
});
export type CustomerListQuery = z.infer<typeof CustomerListQuerySchema>;

export const CustomerListResponseSchema = paginatedResponseSchema(CustomerListItemSchema);
export type CustomerListResponse = z.infer<typeof CustomerListResponseSchema>;

export const CustomerDetailSchema = z.object({
  id: UuidSchema,
  kind: CustomerKindSchema,
  displayName: z.string(),
  /** Optional override for itinerary emails / share pages. */
  primaryName: z.string().nullable().optional(),
  salutation: z.string().nullable(),
  firstName: z.string().nullable(),
  middleName: z.string().nullable(),
  lastName: z.string().nullable(),
  email: StoredEmailSchema.nullable(),
  // 2026-06-25. Persistent secondary email captured at the lead form.
  // The form's "Mark as primary email" checkbox on save swaps this
  // onto `email` (and the old `email` lands here).
  alternateEmail: StoredEmailSchema.nullable(),
  mobileCountry: z.string().nullable(),
  mobileNumber: z.string().nullable(),
  altPhone: z.string().nullable(),
  dateOfBirth: z.iso.date().nullable(),
  gender: GenderSchema.nullable(),
  maritalStatus: MaritalStatusSchema.nullable(),
  nationality: CountryRefSchema.nullable(),
  passportNumber: z.string().nullable(),
  panNumber: z.string().nullable(),
  gstNumber: z.string().nullable(),
  companyName: z.string().nullable(),
  contactPerson: z.string().nullable(),
  branch: NamedRefSchema.nullable(),
  linkedCustomerId: UuidSchema.nullable(),
  notes: z.string().nullable(),
  isActive: z.boolean(),
  addresses: z.array(CustomerAddressSchema),
  contacts: z.array(CustomerContactSchema),
  documents: z.array(CustomerDocumentSchema),
  createdAt: IsoDateTimeSchema,
  updatedAt: IsoDateTimeSchema,
  deletedAt: IsoDateTimeSchema.nullable(),
});
export type CustomerDetail = z.infer<typeof CustomerDetailSchema>;

// ---------------------------------------------------------------------------
// Create / update
// ---------------------------------------------------------------------------

const CustomerCoreFieldsSchema = z.object({
  kind: CustomerKindSchema.default('INDIVIDUAL'),
  salutation: z.string().max(20).nullable().optional(),
  /** Optional override for user-facing itinerary emails / share pages. */
  primaryName: z.string().trim().max(200).nullable().optional(),
  firstName: z.string().max(80).nullable().optional(),
  middleName: z.string().max(80).nullable().optional(),
  lastName: z.string().max(80).nullable().optional(),
  email: EmailSchema.nullable().optional(),
  alternateEmail: EmailSchema.nullable().optional(),
  mobileCountry: MobileCountrySchema.nullable().optional(),
  mobileNumber: MobileNumberSchema.nullable().optional(),
  altPhone: z.string().max(30).nullable().optional(),
  dateOfBirth: z.iso.date().nullable().optional(),
  gender: GenderSchema.nullable().optional(),
  maritalStatus: MaritalStatusSchema.nullable().optional(),
  nationalityCountryId: UuidSchema.nullable().optional(),
  passportNumber: PassportNumberSchema.nullable().optional(),
  panNumber: PanNumberSchema.nullable().optional(),
  gstNumber: GstNumberSchema.nullable().optional(),
  companyName: z.string().max(160).nullable().optional(),
  contactPerson: z.string().max(120).nullable().optional(),
  branchId: UuidSchema.nullable().optional(),
  linkedCustomerId: UuidSchema.nullable().optional(),
  notes: z.string().max(4000).nullable().optional(),
});

/**
 * Server computes display_name. Client may pass first/last/middle (INDIVIDUAL)
 * or companyName (CORPORATE). At least one identifying field is required.
 */
export const CreateCustomerRequestSchema = CustomerCoreFieldsSchema.refine(
  (v) => {
    if (v.kind === 'CORPORATE') {
      return Boolean(v.companyName && v.companyName.trim().length > 0);
    }
    return Boolean(
      (v.firstName && v.firstName.trim().length > 0) ||
      (v.lastName && v.lastName.trim().length > 0),
    );
  },
  {
    message:
      'INDIVIDUAL customers need firstName or lastName; CORPORATE customers need companyName',
    path: ['firstName'],
  },
);
export type CreateCustomerRequest = z.infer<typeof CreateCustomerRequestSchema>;

export const UpdateCustomerRequestSchema = CustomerCoreFieldsSchema.partial();
export type UpdateCustomerRequest = z.infer<typeof UpdateCustomerRequestSchema>;
